/* eslint-disable camelcase */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import ReactTable from 'react-table';
import classNames from 'classnames';
import 'react-table/react-table.css';

import CheckboxCustom from '../components/forms/checkbox_custom';
import filters from './filters';
import './dashboard_table.scss';

class DashboardTable extends Component {
  constructor(props) {
    super(props);

    this.reactTableRef = null;

    this.MIN_SELECT_COLUMN_WIDTH = 40;

    this.state = {
      selectedAll: false,
    };

    this.extendWithSelectable = this.extendWithSelectable.bind(this);
    this.getSortedDataIds = this.getSortedDataIds.bind(this);
    this.onFilteredChange = this.onFilteredChange.bind(this);
    this.renderSelectableCell = this.renderSelectableCell.bind(this);
    this.renderSelectableHeader = this.renderSelectableHeader.bind(this);
    this.setAllRows = this.setAllRows.bind(this);
    this.toggleSelectAll = this.toggleSelectAll.bind(this);
    this.toggleSelectSingle = this.toggleSelectSingle.bind(this);
    this.updateSelectedIds = this.updateSelectedIds.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { filtered: prevFiltered, selectedIds: prevSelectedIds, data: prevData } = prevProps;
    const { filtered, selectedIds, data } = this.props;
    const { selectedAll } = this.state;

    if (selectedAll) {
      if (_.size(prevFiltered) !== _.size(filtered)) {
        this.setAllRows(selectedAll);
      }

      if (_.isEmpty(data)) {
        this.setAllRows(false);
      }
    }

    if (!_.isEmpty(selectedIds) && _.size(data) !== _.size(prevData)) {
      const dataIds = _.map(data, 'id');
      const idsIntersection = _.intersection(dataIds, selectedIds);
      const shouldUpdateSelectedIds = !_.isEqual(idsIntersection, selectedIds);

      if (shouldUpdateSelectedIds) {
        this.updateSelectedIds(idsIntersection);
      }
    }

    if (!_.isEqual(prevSelectedIds, selectedIds)) {
      this.updateSelectedIds(selectedIds);
    }
  }

  onFilteredChange(filtered) {
    const { onFilterChange, selectedIds } = this.props;
    const { selectedAll } = this.state;

    const sortedDataIds = this.getSortedDataIds();
    const newSelectedIds = _.filter(sortedDataIds, sortedDataId => {
      if (selectedAll) {
        return true;
      }

      return _.indexOf(selectedIds, sortedDataId) !== -1;
    });

    this.props.onSelectedChange(newSelectedIds);
    if (_.isFunction(onFilterChange)) {
      onFilterChange(filtered);
    }
  }

  getSortedDataIds() {
    const { reactTableRef } = this;

    if (reactTableRef) {
      const tableState = reactTableRef.getResolvedState();

      return _.map(tableState.sortedData, dataRow => dataRow.id);
    }

    return [];
  }

  setAllRows(selectedAll = true) {
    this.props.onSelectedChange();

    if (selectedAll) {
      this.props.onSelectedChange(this.getSortedDataIds());
    }

    this.setState({
      selectedAll,
    });
  }

  updateSelectedIds(newSelectedIds) {
    const { onSelectedChange } = this.props;
    if (_.isFunction(onSelectedChange)) {
      onSelectedChange(newSelectedIds);
    }
  }

  toggleSelectAll() {
    const { selectedAll } = this.state;

    this.setAllRows(!selectedAll);
  }

  toggleSelectSingle(recordId) {
    let newSelectedIds = [];
    let newSelectedAll = this.state.selectedAll;
    const selectedIds = _.get(this.props, 'selectedIds', []);

    if (_.indexOf(selectedIds, recordId) === -1) {
      newSelectedIds = [...selectedIds, recordId];
    } else {
      newSelectedIds = _.filter(selectedIds, id => id !== recordId);
      newSelectedAll = false;
    }
    this.setState({ selectedAll: newSelectedAll });
    this.props.onSelectedChange(newSelectedIds);
  }

  extendWithSelectable(columns) {
    return [
      {
        accessor: 'id',
        Cell: this.renderSelectableCell,
        className: 'rt-td--selectable-cell',
        filterable: false,
        Header: this.renderSelectableHeader,
        headerClassName: 'rt-td--selectable-cell',
        id: 'id',
        sortable: false,
        width: this.MIN_SELECT_COLUMN_WIDTH,
      },
      ...columns,
    ];
  }

  renderSelectableCell(cell) {
    const { selectedIds } = this.props;

    const isChecked = _.indexOf(selectedIds, cell.value) !== -1;
    const name = `dashboard-table-selectable-${cell.value}`;

    return (
      <CheckboxCustom
        checked={isChecked}
        id={name}
        name={name}
        onChange={() => this.toggleSelectSingle(cell.value)}
      />
    );
  }

  renderSelectableHeader() {
    const { selectedAll } = this.state;

    const name = 'dashboard-table-selectable-header';

    return (
      <CheckboxCustom checked={selectedAll} id={name} name={name} onChange={this.toggleSelectAll} />
    );
  }

  render() {
    const {
      className,
      columns,
      data,
      defaultFilter,
      defaultPageSize,
      mobile,
      filterable,
      pageSizeOptions,
      selectable,
      showPagination,
      showPageSizeOptions,
      ...otherProps
    } = this.props;

    const tableColumns = selectable ? this.extendWithSelectable(columns) : columns;

    return (
      <ReactTable
        key="dashboard-table"
        className={classNames(className, {
          'noguchi-table': !mobile,
          'noguchi-table--mobile': mobile,
        })}
        columns={tableColumns}
        data={data}
        defaultFilterMethod={defaultFilter}
        defaultPageSize={defaultPageSize}
        filterAll
        filterable={filterable}
        minRows={0}
        onFilteredChange={this.onFilteredChange}
        pageSizeOptions={pageSizeOptions}
        ref={ref => {
          this.reactTableRef = ref;
        }}
        showPagination={showPagination && defaultPageSize && _.size(data) > defaultPageSize}
        showPageSizeOptions={showPageSizeOptions}
        {...otherProps}
      />
    );
  }
}

DashboardTable.propTypes = {
  className: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({})),
  data: PropTypes.arrayOf(PropTypes.shape({})),
  defaultFilter: PropTypes.func,
  defaultPageSize: PropTypes.number,
  filterable: PropTypes.bool,
  filtered: PropTypes.arrayOf(PropTypes.shape({})),
  mobile: PropTypes.bool,
  onFilterChange: PropTypes.func,
  onSelectedChange: PropTypes.func,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  selectable: PropTypes.bool,
  selectedIds: PropTypes.arrayOf(PropTypes.number),
  showPageSizeOptions: PropTypes.bool,
  showPagination: PropTypes.bool,
};

DashboardTable.defaultProps = {
  defaultFilter: filters.defaultFilter,
  defaultPageSize: 100,
  filterable: false,
  mobile: false,
  onFilterChange: () => {},
  pageSizeOptions: [50, 100, 200, 300],
  selectable: false,
  showPagination: true,
  showPageSizeOptions: true,
};

export default DashboardTable;
